import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, Plus, RefreshCw } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Label,
  Textarea,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { formatDate } from '@/lib/format-date';
import { useCertificates, useCreateCertificate, useUpdateCertificate, useDeleteCertificate, type Certificate } from '@/api/opennebula';
import { RowMenu } from './components/CrudBits';
import { ListPageShell, TableSkeleton, ErrorState, Dash } from './components/ListPageShell';

export function Certificates() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: certs = [], isLoading, isError, refetch } = useCertificates(serviceId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [rename, setRename] = useState<Certificate | null>(null);
  const [del, setDel] = useState<Certificate | null>(null);

  const remove = useDeleteCertificate(serviceId!, {
    onSuccess: () => { show({ tone: 'success', title: 'Certificate deleted' }); setDel(null); },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const columns: ColumnDef<Certificate, unknown>[] = [
    { id: 'id', header: 'ID', cell: ({ row }) => <code style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.id}</code> },
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || row.original.id}</span> },
    { id: 'type', header: 'Type', cell: ({ row }) => (row.original.type ? <Badge tone="info" appearance="subtle" size="sm">{row.original.type}</Badge> : <Dash />) },
    { id: 'fingerprint', header: 'Fingerprint', cell: ({ row }) => <code style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.fingerprint || '—'}</code> },
    { id: 'created', header: 'Created', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.created_at ? formatDate(row.original.created_at) : '—'}</span> },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'Rename', onSelect: () => setRename(row.original) }, { label: 'Delete', danger: true, onSelect: () => setDel(row.original) }]} /> },
  ];

  const uploadBtn = (
    <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setUploadOpen(true)}>
      Upload certificate
    </Button>
  );

  return (
    <ListPageShell
      icon={<ShieldCheck size={22} />}
      title="SSL/TLS certificates"
      description="Certificates available to load balancers and services."
      actions={
        <>
          <Button size="sm" variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>
            Refresh
          </Button>
          {uploadBtn}
        </>
      }
    >
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <ErrorState description="Could not load certificates for this service." onRetry={() => refetch()} />
      ) : (
        // DataTable owns the bordered frame; the empty placeholder renders
        // inside it via `emptyState`, so empty and populated share one frame
        // (no extra Card wrapper, no double border).
        <DataTable
          columns={columns}
          data={certs}
          getRowId={(c) => String(c.id)}
          emptyState={
            <EmptyState
              icon={<ShieldCheck size={28} />}
              title="No certificates"
              description="Upload a PEM certificate and private key."
              actions={uploadBtn}
            />
          }
        />
      )}
      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} serviceId={serviceId!} />
      {rename && <RenameDialog serviceId={serviceId!} cert={rename} onClose={() => setRename(null)} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete certificate?" description={`Remove “${del?.name || del?.id}”?`} confirmLabel="Delete" destructive loading={remove.isPending} onConfirm={() => del && remove.mutate(del.id)} />
    </ListPageShell>
  );
}

function UploadDialog({ open, onOpenChange, serviceId }: { open: boolean; onOpenChange: (o: boolean) => void; serviceId: string }) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [cert, setCert] = useState('');
  const [key, setKey] = useState('');
  const [chain, setChain] = useState('');

  const reset = () => { setName(''); setCert(''); setKey(''); setChain(''); };

  const create = useCreateCertificate(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Certificate uploaded' }); onOpenChange(false); reset(); },
    onError: () => show({ tone: 'danger', title: 'Upload failed' }),
  });

  const valid = name.trim() && cert.includes('BEGIN') && key.includes('BEGIN');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>Upload certificate</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div>
              <Label htmlFor="cert-name">Name</Label>
              <Input id="cert-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="example.com" />
            </div>
            <div>
              <Label htmlFor="cert-pem">Certificate (PEM)</Label>
              <Textarea id="cert-pem" value={cert} onChange={(e) => setCert(e.target.value)} rows={4} placeholder="-----BEGIN CERTIFICATE-----" style={mono} />
            </div>
            <div>
              <Label htmlFor="cert-key">Private key (PEM)</Label>
              <Textarea id="cert-key" value={key} onChange={(e) => setKey(e.target.value)} rows={4} placeholder="-----BEGIN PRIVATE KEY-----" style={mono} />
            </div>
            <div>
              <Label htmlFor="cert-chain">Certificate chain (PEM)</Label>
              <Textarea id="cert-chain" value={chain} onChange={(e) => setChain(e.target.value)} rows={3} placeholder="-----BEGIN CERTIFICATE----- (optional intermediate CA chain)" style={mono} />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={!valid || create.isPending}
            onClick={() => create.mutate({ name: name.trim(), certificate: cert, privkey: key, ...(chain.trim() ? { chain: chain.trim() } : {}) })}
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameDialog({ serviceId, cert, onClose }: { serviceId: string; cert: Certificate; onClose: () => void }) {
  const { show } = useToast();
  const [name, setName] = useState(cert.name ?? '');
  const update = useUpdateCertificate(serviceId, cert.id, {
    onSuccess: () => { show({ tone: 'success', title: 'Certificate renamed' }); onClose(); },
    onError: () => show({ tone: 'danger', title: 'Rename failed' }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Rename certificate</DialogTitle></DialogHeader>
        <DialogBody>
          <Label htmlFor="cert-rename">Name</Label>
          <Input id="cert-rename" value={name} onChange={(e) => setName(e.target.value)} />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim() || update.isPending} onClick={() => update.mutate({ name: name.trim() })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const mono: React.CSSProperties = { fontFamily: 'var(--vn-font-mono, monospace)' };
